import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Upload,
  X,
  Star,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Save,
  Loader2,
  Tag,
  IndianRupee,
  FileText,
  Camera,
  Package,
  Key,
  ShoppingBag,
  Calendar,
  Settings,
  AlertTriangle,
} from "lucide-react";
import { device } from "../../../atoms/Utlis/media";
import BrandDatePicker from "../../../atoms/Utlis/BrandDatePicker";
import {
  getMarketplaceProfile,
  createListing,
  updateListing,
  getListingDetail,
  getMarketplaceCategories,
  ListingType,
  ListingStatus,
  PriceUnit,
  MarketplaceCategory,
  CreateListingPayload,
  MarketplaceProfile,
} from "../../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { uploadToCloudinary1 } from "../../../../Redux/ApiCalls/Cloudinary";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import { resolveImage } from "../../../../Utils/helper";
import {
  PageContainer,
  FormScrollContainer,
  PageTitle,
  FormHero,
  FormHeroGlow,
  FormHeroRow,
  FormBackButton,
  FormHeroText,
  FormHeroBadge,
  FormHeroSubtitle,
  FormCard,
  SectionHeading,
  SectionIcon,
  SectionHeadingBody,
  SectionHeadingTitle,
  SectionHeadingSub,
  RowGrid,
  FormGroup,
  FormLabel,
  TextInput,
  TextAreaInput,
  SelectDropdown,
  FooterButtonsRow,
  SubmitHint,
  SaveButton,
  Req,
  gradient,
} from "../formSharedStyles";

const EndFooter = styled(FooterButtonsRow)`
  position: static;
  margin-top: 20px;
  margin-bottom: 8px;
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

interface FormData {
  listing_type: ListingType;
  title: string;
  description: string;
  category: string;
  price: string;
  price_unit: PriceUnit;
  location: string;
  status: ListingStatus;
  image_url: string;
  image_urls: string[];
  metadata_brand: string;
  metadata_model: string;
  available_from: string;
  available_to: string;
}

const defaultForm: FormData = {
  listing_type: "sale",
  title: "",
  description: "",
  category: "",
  price: "",
  price_unit: "fixed",
  location: "",
  status: "active",
  image_url: "",
  image_urls: [],
  metadata_brand: "",
  metadata_model: "",
  available_from: "",
  available_to: "",
};

const MarketplaceListingFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState<FormData>(defaultForm);
  const [categories, setCategories] = useState<MarketplaceCategory[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [myProfile, setMyProfile] = useState<MarketplaceProfile | null>(null);

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [previewModalIndex, setPreviewModalIndex] = useState<number | null>(null);

  const openPreview = (index: number) => setPreviewModalIndex(index);
  const closePreview = () => setPreviewModalIndex(null);

  const showNextPreview = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (previewModalIndex === null || form.image_urls.length === 0) return;
    setPreviewModalIndex((prev) => (prev !== null ? (prev + 1) % form.image_urls.length : 0));
  };

  const showPrevPreview = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (previewModalIndex === null || form.image_urls.length === 0) return;
    setPreviewModalIndex((prev) =>
      prev !== null ? (prev - 1 + form.image_urls.length) % form.image_urls.length : 0
    );
  };

  useEffect(() => {
    if (previewModalIndex === null) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePreview();
      if (e.key === "ArrowRight") showNextPreview();
      if (e.key === "ArrowLeft") showPrevPreview();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [previewModalIndex, form.image_urls.length]);

  useEffect(() => {
    getMarketplaceProfile()
      .then((res) => {
        if (res?.data) setMyProfile(res.data);
      })
      .catch(() => {});
    getMarketplaceCategories().then(setCategories).catch(() => {});
    if (isEdit && id) {
      setLoading(true);
      getListingDetail(id)
        .then((data) => {
          const rawUrls = data.metadata?.image_urls || data.metadata?.images;
          let parsedUrls: string[] = [];
          if (Array.isArray(rawUrls)) {
            parsedUrls = rawUrls;
          } else if (typeof rawUrls === "string") {
            try {
              const p = JSON.parse(rawUrls);
              if (Array.isArray(p)) parsedUrls = p;
              else if (rawUrls.includes(",")) parsedUrls = rawUrls.split(",").map((u) => u.trim());
              else parsedUrls = [rawUrls];
            } catch {
              parsedUrls = rawUrls.includes(",")
                ? rawUrls.split(",").map((u) => u.trim())
                : [rawUrls];
            }
          }
          let loadedUrls = parsedUrls.filter(Boolean);
          if (data.image_url) {
            if (!loadedUrls.includes(data.image_url)) {
              loadedUrls = [data.image_url, ...loadedUrls];
            } else if (loadedUrls[0] !== data.image_url) {
              loadedUrls = [data.image_url, ...loadedUrls.filter((u) => u !== data.image_url)];
            }
          }
          if (loadedUrls.length === 0 && data.image_url) {
            loadedUrls = [data.image_url];
          }

          setForm({
            listing_type: data.listing_type,
            title: data.title,
            description: data.description || "",
            category: String(data.category || ""),
            price: data.price,
            price_unit: data.price_unit,
            location: data.location || "",
            status: data.status,
            image_url: data.image_url || loadedUrls[0] || "",
            image_urls: loadedUrls,
            metadata_brand: data.metadata?.brand || "",
            metadata_model: data.metadata?.model || "",
            available_from: data.available_from ? data.available_from.substring(0, 10) : "",
            available_to: data.available_to ? data.available_to.substring(0, 10) : "",
          });
        })
        .catch(() => showErrorToast("Failed to load listing"))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const set = (key: keyof FormData, val: string) => setForm((prev) => ({ ...prev, [key]: val }));

  const isEquipmentType = form.listing_type === "sale" || form.listing_type === "rent";

  const getDaysCount = (fromStr: string, toStr: string) => {
    if (!fromStr || !toStr) return 0;
    const fromDate = new Date(fromStr);
    const toDate = new Date(toStr);
    if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) return 0;
    const diffTime = toDate.getTime() - fromDate.getTime();
    if (diffTime < 0) return 0;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const setImageUrls = (urls: string[]) => {
    setForm((prev) => ({
      ...prev,
      image_urls: urls,
      image_url: urls[0] || "",
    }));
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", `${index}`);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  const handleDragLeave = () => setDragOverIndex(null);

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    setImageUrls(
      (() => {
        const updated = [...form.image_urls];
        const [movedItem] = updated.splice(draggedIndex, 1);
        updated.splice(targetIndex, 0, movedItem);
        return updated;
      })()
    );

    if (targetIndex === 0 || draggedIndex === 0) {
      showSuccessToast("Primary image updated!");
    } else {
      showSuccessToast("Images reordered!");
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) {
      e.target.value = "";
      return;
    }
    const currentCount = form.image_urls.length;
    const remainingSlots = 5 - currentCount;
    if (remainingSlots <= 0) {
      showErrorToast("Maximum 5 images allowed");
      e.target.value = "";
      return;
    }
    const isExceeding = files.length > remainingSlots;
    const filesToUpload = isExceeding ? files.slice(0, remainingSlots) : files;

    setImageUploading(true);
    try {
      const urls: string[] = [];
      for (const file of filesToUpload) {
        urls.push(await uploadToCloudinary1(file));
      }
      setImageUrls([...form.image_urls, ...urls]);

      if (isExceeding) {
        showSuccessToast(
          remainingSlots === 5
            ? "You can upload max 5 images. First 5 images are uploaded"
            : `You can upload max 5 images. First ${remainingSlots} images are uploaded`
        );
      } else {
        showSuccessToast(filesToUpload.length === 1 ? "Image uploaded" : "Images uploaded");
      }
    } catch {
      showErrorToast("Image upload failed");
    } finally {
      setImageUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (url: string) => {
    setImageUrls(form.image_urls.filter((item) => item !== url));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (myProfile?.is_active === false) {
      showErrorToast("Your marketplace account is frozen or deactivated. You cannot save listings.");
      return;
    }
    if (!form.title.trim()) {
      showErrorToast("Title is required");
      return;
    }
    if (form.image_urls.length === 0) {
      showErrorToast("Please upload at least one listing image");
      return;
    }

    setSubmitting(true);
    try {
      const payload: CreateListingPayload = {
        listing_type: form.listing_type,
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        category: form.category ? Number(form.category) : undefined,
        price: form.price || undefined,
        price_unit: form.price_unit,
        location: form.location.trim() || undefined,
        status: form.status,
        image_url: form.image_urls[0] || undefined,
        available_from:
          form.listing_type === "rent" && form.available_from ? form.available_from : undefined,
        available_to:
          form.listing_type === "rent" && form.available_to ? form.available_to : undefined,
      };

      const metadata: Record<string, any> = { image_urls: form.image_urls };
      if (isEquipmentType) {
        if (form.metadata_brand) metadata.brand = form.metadata_brand;
        if (form.metadata_model) metadata.model = form.metadata_model;
      }
      if (Object.keys(metadata).length > 0) {
        payload.metadata = metadata;
      }

      if (isEdit && id) {
        await updateListing(id, payload);
        showSuccessToast("Listing updated!");
      } else {
        await createListing(payload);
        showSuccessToast("Listing created!");
      }
      navigate("/dashboard/marketplace/listings");
    } catch (err: any) {
      showErrorToast(err?.response?.data?.message || "Failed to save listing");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingWrap>
          <Loader2 size={28} color="#c94daa" className="spin" />
          Loading listing details…
        </LoadingWrap>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <FormHero>
        <FormHeroGlow />
        <FormHeroRow>
          <FormBackButton
            type="button"
            onClick={() => navigate("/dashboard/marketplace/listings")}
            aria-label="Go back"
          >
            <ArrowLeft size={18} />
          </FormBackButton>
          <FormHeroText>
            <FormHeroBadge>
              <Sparkles size={11} />
              Marketplace
            </FormHeroBadge>
            <PageTitle className="linear-text">{isEdit ? "Edit Listing" : "New Listing"}</PageTitle>
            <FormHeroSubtitle>
              {isEdit
                ? "Update your marketplace listing details, pricing, and gallery."
                : "Create a listing for equipment sale, rental, or photography services."}
            </FormHeroSubtitle>
          </FormHeroText>
        </FormHeroRow>
      </FormHero>

      <FormScrollContainer>
        {myProfile?.is_active === false && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "14px 18px",
              marginBottom: "16px",
              borderRadius: "10px",
              background: "rgba(239, 68, 68, 0.12)",
              border: "1px solid rgba(239, 68, 68, 0.35)",
              color: "#fca5a5",
              fontSize: "14px",
              lineHeight: "1.5",
            }}
          >
            <AlertTriangle size={20} style={{ flexShrink: 0, color: "#ef4444" }} />
            <div>
              <strong>Account Frozen:</strong> Your marketplace account has been deactivated/frozen by an administrator.
              You cannot create new listings or edit listings.
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <FormCard>
            <SectionHeading>
              <SectionIcon $tone="#7c4dff">
                <Tag size={20} />
              </SectionIcon>
              <SectionHeadingBody>
                <SectionHeadingTitle>Listing type</SectionHeadingTitle>
                <SectionHeadingSub>Choose whether you are renting or selling equipment</SectionHeadingSub>
              </SectionHeadingBody>
            </SectionHeading>

            <TypeGrid>
              {(
                [
                  { value: "rent", label: "For Rent", icon: Key },
                  { value: "sale", label: "For Sale", icon: ShoppingBag },
                ] as { value: ListingType; label: string; icon: typeof Key }[]
              ).map((lt) => {
                const Icon = lt.icon;
                return (
                  <TypeChip
                    key={lt.value}
                    type="button"
                    $active={form.listing_type === lt.value}
                    onClick={() => set("listing_type", lt.value)}
                  >
                    <Icon size={16} />
                    {lt.label}
                  </TypeChip>
                );
              })}
            </TypeGrid>
          </FormCard>

          <FormCard>
            <SectionHeading>
              <SectionIcon>
                <FileText size={20} />
              </SectionIcon>
              <SectionHeadingBody>
                <SectionHeadingTitle>Basic details</SectionHeadingTitle>
                <SectionHeadingSub>Title, location, and description shown to buyers</SectionHeadingSub>
              </SectionHeadingBody>
            </SectionHeading>

            <RowGrid>
              <FormGroup>
                <FormLabel>
                  Title <Req>*</Req>
                </FormLabel>
                <TextInput
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  placeholder={`e.g. ${
                    form.listing_type === "rent" ? "Sony 24-70mm f/2.8 Rental" : "Sony A7 IV Body"
                  }`}
                  required
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>Location</FormLabel>
                <TextInput
                  value={form.location}
                  onChange={(e) => set("location", e.target.value)}
                  placeholder="e.g. Mumbai, Maharashtra"
                />
              </FormGroup>
            </RowGrid>

            <FormGroup style={{ marginTop: 16 }}>
              <FormLabel>Description</FormLabel>
              <TextAreaInput
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder="Describe your listing in detail..."
                rows={4}
              />
            </FormGroup>
          </FormCard>

          <FormCard>
            <SectionHeading>
              <SectionIcon $tone="#31a66a">
                <IndianRupee size={20} />
              </SectionIcon>
              <SectionHeadingBody>
                <SectionHeadingTitle>Pricing</SectionHeadingTitle>
                <SectionHeadingSub>Set price and billing unit for this listing</SectionHeadingSub>
              </SectionHeadingBody>
            </SectionHeading>

            <RowGrid>
              <FormGroup>
                <FormLabel>Price (₹)</FormLabel>
                <TextInput
                  type="number"
                  min="0"
                  value={form.price}
                  onChange={(e) => set("price", e.target.value)}
                  placeholder="e.g. 5000"
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>Price Unit</FormLabel>
                <SelectDropdown
                  value={form.price_unit}
                  onChange={(e) => set("price_unit", e.target.value as PriceUnit)}
                >
                  <option value="fixed">Fixed</option>
                  <option value="per_day">Per Day</option>
                  <option value="per_hour">Per Hour</option>
                  <option value="negotiable">Negotiable</option>
                </SelectDropdown>
              </FormGroup>
            </RowGrid>

            {form.listing_type === "rent" && (
              <div style={{ marginTop: 16 }}>
                <RowGrid>
                  <FormGroup>
                    <FormLabel>Available From</FormLabel>
                    <BrandDatePicker
                      value={form.available_from}
                      onChange={(v) => set("available_from", v)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Available To</FormLabel>
                    <BrandDatePicker
                      value={form.available_to}
                      onChange={(v) => set("available_to", v)}
                    />
                  </FormGroup>
                </RowGrid>
                {form.available_from && form.available_to && (
                  <DurationBadge>
                    <Calendar size={14} />
                    Rental duration: {getDaysCount(form.available_from, form.available_to)} days
                  </DurationBadge>
                )}
              </div>
            )}
          </FormCard>

          {isEquipmentType && (
            <FormCard>
              <SectionHeading>
                <SectionIcon $tone="#35b7c8">
                  <Package size={20} />
                </SectionIcon>
                <SectionHeadingBody>
                  <SectionHeadingTitle>Equipment details</SectionHeadingTitle>
                  <SectionHeadingSub>Category, brand, and model information</SectionHeadingSub>
                </SectionHeadingBody>
              </SectionHeading>

              <ThreeCol>
                <FormGroup>
                  <FormLabel>Category</FormLabel>
                  <SelectDropdown
                    value={form.category}
                    onChange={(e) => set("category", e.target.value)}
                  >
                    <option value="">Select category</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </SelectDropdown>
                </FormGroup>
                <FormGroup>
                  <FormLabel>Brand</FormLabel>
                  <TextInput
                    value={form.metadata_brand}
                    onChange={(e) => set("metadata_brand", e.target.value)}
                    placeholder="e.g. Sony"
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Model</FormLabel>
                  <TextInput
                    value={form.metadata_model}
                    onChange={(e) => set("metadata_model", e.target.value)}
                    placeholder="e.g. A7 IV"
                  />
                </FormGroup>
              </ThreeCol>
            </FormCard>
          )}

          <FormCard>
            <SectionHeading>
              <SectionIcon $tone="#f09a3e">
                <Camera size={20} />
              </SectionIcon>
              <SectionHeadingBody>
                <SectionHeadingTitle>Listing images</SectionHeadingTitle>
                <SectionHeadingSub>
                  Upload up to 5 photos. Drag to reorder — first image is primary.
                </SectionHeadingSub>
              </SectionHeadingBody>
            </SectionHeading>

            <ImageUploadArea>
              {form.image_urls.length > 0 && (
                <>
                  <ImagePreviewGrid>
                    {form.image_urls.map((url, index) => (
                      <ImagePreview
                        key={url}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        onClick={() => openPreview(index)}
                        $isDragging={draggedIndex === index}
                        $isDragOver={dragOverIndex === index && draggedIndex !== index}
                        title="Click to preview • Drag to reorder"
                      >
                        <PreviewImg src={resolveImage(url)} alt={`Listing ${index + 1}`} />
                        <RemoveImgBtn
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeImage(url);
                          }}
                          title="Remove photo"
                        >
                          <X size={14} />
                        </RemoveImgBtn>
                        {index === 0 ? (
                          <PrimaryBadge>
                            <Star size={10} fill="#ffffff" color="#ffffff" /> #1 Primary
                          </PrimaryBadge>
                        ) : (
                          <NumberBadge>#{index + 1}</NumberBadge>
                        )}
                      </ImagePreview>
                    ))}
                  </ImagePreviewGrid>
                  <GalleryHintText>
                    Tap any photo to preview full size. Drag photos to reorder or change the{" "}
                    <strong>#1 Primary</strong> image.
                  </GalleryHintText>
                </>
              )}
              {form.image_urls.length < 5 && (
                <UploadLabel htmlFor="listing-image-upload">
                  {imageUploading ? (
                    <UploadingText>
                      <Loader2 size={18} className="spin" /> Uploading…
                    </UploadingText>
                  ) : (
                    <>
                      <Upload size={22} color="rgba(23,23,23,0.35)" />
                      <UploadHint>Click to upload images</UploadHint>
                      <UploadSub>PNG, JPG, WEBP — {form.image_urls.length}/5 uploaded</UploadSub>
                    </>
                  )}
                  <input
                    id="listing-image-upload"
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleImageUpload}
                    disabled={imageUploading}
                  />
                </UploadLabel>
              )}
            </ImageUploadArea>
          </FormCard>

          <FormCard>
            <SectionHeading>
              <SectionIcon>
                <Settings size={20} />
              </SectionIcon>
              <SectionHeadingBody>
                <SectionHeadingTitle>Visibility</SectionHeadingTitle>
                <SectionHeadingSub>Control whether this listing is live on the marketplace</SectionHeadingSub>
              </SectionHeadingBody>
            </SectionHeading>

            <RowGrid>
              <FormGroup>
                <FormLabel>Status</FormLabel>
                <SelectDropdown
                  value={form.status}
                  onChange={(e) => set("status", e.target.value as ListingStatus)}
                >
                  <option value="draft">Draft (not visible publicly)</option>
                  <option value="active">Active (live on marketplace)</option>
                  <option value="inactive">Inactive</option>
                </SelectDropdown>
              </FormGroup>
            </RowGrid>
          </FormCard>

          <EndFooter>
            <SubmitHint>
              {isEdit
                ? "Review listing details and gallery, then update your marketplace listing."
                : "Add at least one image, then publish your listing to the marketplace."}
            </SubmitHint>
            <SaveButton
              type="submit"
              disabled={submitting || imageUploading || myProfile?.is_active === false}
              style={{
                opacity: myProfile?.is_active === false ? 0.5 : 1,
                cursor: myProfile?.is_active === false ? "not-allowed" : "pointer",
              }}
            >
              {submitting ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
              {submitting ? "Saving…" : isEdit ? "Update Listing" : "Create Listing"}
            </SaveButton>
          </EndFooter>
        </form>
      </FormScrollContainer>

      {previewModalIndex !== null && form.image_urls[previewModalIndex] && (
        <LightboxOverlay onClick={closePreview}>
          <LightboxTopBar onClick={(e) => e.stopPropagation()}>
            <LightboxBadge>
              {previewModalIndex === 0 ? (
                <>
                  <Star size={13} fill="#ffc951" color="#ffc951" />
                  <span>#1 Primary Image</span>
                </>
              ) : (
                <span>Photo #{previewModalIndex + 1}</span>
              )}
            </LightboxBadge>
            <LightboxCounter>
              {previewModalIndex + 1} / {form.image_urls.length}
            </LightboxCounter>
            <LightboxCloseBtn onClick={closePreview} title="Close (Esc)">
              <X size={20} />
            </LightboxCloseBtn>
          </LightboxTopBar>

          <LightboxMainArea onClick={(e) => e.stopPropagation()}>
            <LightboxImg
              src={resolveImage(form.image_urls[previewModalIndex])}
              alt={`Preview ${previewModalIndex + 1}`}
            />
          </LightboxMainArea>

          {form.image_urls.length > 1 && (
            <>
              <LightboxNavBtn $left onClick={showPrevPreview} title="Previous photo (←)">
                <ChevronLeft size={28} />
              </LightboxNavBtn>
              <LightboxNavBtn $right onClick={showNextPreview} title="Next photo (→)">
                <ChevronRight size={28} />
              </LightboxNavBtn>
            </>
          )}
        </LightboxOverlay>
      )}
    </PageContainer>
  );
};

export default MarketplaceListingFormPage;

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 12px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.55);

  .spin {
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const TypeGrid = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const TypeChip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 18px;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$active ? "transparent" : "rgba(23, 23, 23, 0.12)")};
  background: ${(p) =>
    p.$active
      ? gradient
      : "linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(250, 250, 250, 1) 100%)"};
  color: ${(p) => (p.$active ? "#fff" : "rgba(23, 23, 23, 0.72)")};
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: ${(p) => (p.$active ? "0 8px 18px rgba(201, 77, 170, 0.22)" : "none")};

  &:hover {
    border-color: rgba(201, 77, 170, 0.28);
    transform: translateY(-1px);
  }
`;

const ThreeCol = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media ${device.tablet} {
    grid-template-columns: 1fr;
  }
`;

const DurationBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: 12px;
  background: rgba(201, 77, 170, 0.08);
  border: 1px solid rgba(201, 77, 170, 0.16);
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #c94daa;
`;

const ImageUploadArea = styled.div`
  border: 1.5px dashed rgba(201, 77, 170, 0.22);
  border-radius: 16px;
  overflow: hidden;
  min-height: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 14px;
  background: rgba(201, 77, 170, 0.03);
`;

const UploadLabel = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 120px;
  cursor: pointer;
  padding: 24px;
  border-radius: 12px;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(201, 77, 170, 0.05);
  }
`;

const UploadHint = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.55);
`;

const UploadSub = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  color: rgba(23, 23, 23, 0.4);
`;

const UploadingText = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #c94daa;
  font-weight: 700;

  .spin {
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ImagePreview = styled.div<{ $isDragging?: boolean; $isDragOver?: boolean }>`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 12px;
  overflow: hidden;
  background: #f7f8fc;
  border: 2px solid ${({ $isDragOver }) => ($isDragOver ? "#c94daa" : "transparent")};
  cursor: grab;
  user-select: none;
  opacity: ${({ $isDragging }) => ($isDragging ? 0.4 : 1)};
  transform: ${({ $isDragOver }) => ($isDragOver ? "scale(1.04)" : "scale(1)")};
  box-shadow: ${({ $isDragOver }) => ($isDragOver ? "0 6px 18px rgba(201, 77, 170, 0.25)" : "none")};
  transition: all 0.2s ease;

  &:active {
    cursor: grabbing;
  }
`;

const PreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ImagePreviewGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
`;

const RemoveImgBtn = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.65);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;

  &:hover {
    background: #e44862;
  }
`;

const PrimaryBadge = styled.span`
  position: absolute;
  bottom: 6px;
  left: 6px;
  background: linear-gradient(135deg, #ffc951 0%, #c94daa 100%);
  color: #ffffff;
  padding: 3px 8px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  z-index: 2;
  pointer-events: none;
`;

const NumberBadge = styled.span`
  position: absolute;
  bottom: 6px;
  left: 6px;
  background: rgba(23, 23, 23, 0.78);
  color: #ffffff;
  padding: 3px 8px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 800;
  backdrop-filter: blur(4px);
  display: inline-flex;
  align-items: center;
  z-index: 2;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  pointer-events: none;
`;

const GalleryHintText = styled.p`
  margin: 10px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.55);
  line-height: 1.5;

  strong {
    color: #171717;
  }
`;

const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(244, 246, 250, 0.94);
  backdrop-filter: blur(20px);
  z-index: 999999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px 32px;
  box-sizing: border-box;
  user-select: none;
  animation: ${fadeUp} 0.2s ease-out;
`;

const LightboxTopBar = styled.div`
  position: absolute;
  top: 20px;
  left: 24px;
  right: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  z-index: 1000;
  pointer-events: auto;
`;

const LightboxBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(23, 23, 23, 0.1);
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
  color: #171717;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
`;

const LightboxCounter = styled.span`
  font-size: 14px;
  font-weight: 800;
  color: #171717;
  background: rgba(255, 255, 255, 0.92);
  padding: 6px 16px;
  border-radius: 999px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
`;

const LightboxCloseBtn = styled.button`
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(23, 23, 23, 0.1);
  color: #171717;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);

  &:hover {
    background: #e44862;
    border-color: #e44862;
    color: #ffffff;
    transform: scale(1.05);
  }
`;

const LightboxMainArea = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  max-width: 1200px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  min-height: 0;
`;

const LightboxImg = styled.img`
  width: min(85vw, 1000px);
  height: min(72vh, 720px);
  object-fit: contain;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid rgba(23, 23, 23, 0.08);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.12);
  animation: ${fadeUp} 0.2s ease-out;
  display: block;
`;

const LightboxNavBtn = styled.button<{ $left?: boolean; $right?: boolean }>`
  position: absolute;
  top: 50%;
  ${({ $left }) => ($left ? "left: 24px;" : "")}
  ${({ $right }) => ($right ? "right: 24px;" : "")}
  transform: translateY(-50%);
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(23, 23, 23, 0.12);
  color: #171717;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  z-index: 1000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);

  &:hover {
    background: #c94daa;
    border-color: #c94daa;
    color: #ffffff;
    transform: translateY(-50%) scale(1.05);
  }
`;
